import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Video, Mic, Play, Square, Save, Trash2, ArrowLeft, 
  CheckCircle, XCircle, Clock, Camera
} from "lucide-react";
import Navbar from "@/components/Navbar";

// Practice Recorder Component
const PracticeRecorder = ({ 
  recordingType, 
  onRecordingComplete,
  onCancel
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const chunksRef = useRef([]);
  
  const [status, setStatus] = useState("idle"); // idle, recording, stopped
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  
  const isVideo = recordingType === "video";
  const timerRef = useRef(null);

  // Draw waveform for voice
  const drawWaveform = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);
      
      ctx.fillStyle = '#18181b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#a855f7';
      ctx.beginPath();
      
      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * canvas.height / 2;
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };
    
    draw();
  }, []);

  const startRecording = async () => {
    setError(null);
    
    try {
      const constraints = isVideo 
        ? { video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (isVideo && videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      } else if (!isVideo) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
        drawWaveform();
      }
      
      chunksRef.current = [];
      const mimeType = isVideo 
        ? (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm')
        : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      
      setStatus("recording");
      setDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Recording error:", err);
      let msg = "Could not start recording.";
      if (err.name === "NotAllowedError") {
        msg = isVideo ? "Camera/mic access denied." : "Microphone access denied.";
      } else if (err.name === "NotFoundError") {
        msg = isVideo ? "No camera found." : "No microphone found.";
      }
      setError(msg);
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;
    
    clearInterval(timerRef.current);
    
    mediaRecorderRef.current.onstop = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      const mimeType = isVideo ? 'video/webm' : 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordedBlob(blob);
      setStatus("stopped");
    };
    
    mediaRecorderRef.current.stop();
  };

  const handleSave = () => {
    if (recordedBlob) {
      onRecordingComplete(recordedBlob, duration);
    }
  };

  const handleReRecord = () => {
    setRecordedBlob(null);
    setStatus("idle");
    setDuration(0);
  };

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      clearInterval(timerRef.current);
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-card/30 rounded-lg border border-white/10 p-6">
      {/* Preview Area */}
      <div className="relative rounded-lg overflow-hidden bg-black aspect-video mb-4">
        {isVideo ? (
          <>
            <video 
              ref={videoRef}
              autoPlay 
              muted 
              playsInline
              className={`w-full h-full object-cover ${status === "stopped" ? "hidden" : ""}`}
              style={{ transform: "scaleX(-1)" }}
            />
            {status === "stopped" && recordedBlob && (
              <video 
                src={URL.createObjectURL(recordedBlob)}
                controls
                className="w-full h-full object-cover"
              />
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-8">
            <Mic className={`w-20 h-20 text-purple-400 mb-4 ${status === 'recording' ? 'animate-pulse' : ''}`} />
            <canvas 
              ref={canvasRef}
              width={500}
              height={120}
              className="w-full max-w-lg rounded bg-zinc-900/50"
            />
            {status === "stopped" && recordedBlob && (
              <audio 
                src={URL.createObjectURL(recordedBlob)}
                controls
                className="mt-4 w-full max-w-md"
              />
            )}
          </div>
        )}
        
        {/* Idle State */}
        {status === "idle" && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center">
              {isVideo ? (
                <Camera className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              ) : (
                <Mic className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              )}
              <p className="text-muted-foreground mb-4">Ready to record your practice session</p>
              <Button onClick={startRecording} size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500">
                {isVideo ? <Video className="w-5 h-5 mr-2" /> : <Mic className="w-5 h-5 mr-2" />}
                Start Recording
              </Button>
            </div>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center p-4">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={() => setError(null)} variant="outline">
                Try Again
              </Button>
            </div>
          </div>
        )}
        
        {/* Recording Indicator */}
        {status === "recording" && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 px-3 py-2 rounded-full">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white font-medium">{formatTime(duration)}</span>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="flex flex-wrap justify-center gap-3">
        {status === "recording" && (
          <Button onClick={stopRecording} variant="destructive" size="lg">
            <Square className="w-5 h-5 mr-2" />
            Stop Recording
          </Button>
        )}
        
        {status === "stopped" && recordedBlob && (
          <>
            <Button onClick={handleSave} size="lg" className="bg-green-600 hover:bg-green-700">
              <Save className="w-5 h-5 mr-2" />
              Save Practice
            </Button>
            <Button onClick={handleReRecord} variant="outline" size="lg">
              <Video className="w-5 h-5 mr-2" />
              Re-record
            </Button>
          </>
        )}
        
        <Button onClick={onCancel} variant="ghost" size="lg">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );
};

// Saved Practice Session Card
const PracticeSessionCard = ({ session, onPlay, onDelete }) => {
  return (
    <div className="bg-card/30 rounded-lg border border-white/10 p-4 hover:border-white/20 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${
          session.recording_type === "video" ? "bg-blue-500/20" : "bg-purple-500/20"
        }`}>
          {session.recording_type === "video" ? (
            <Video className="w-6 h-6 text-blue-400" />
          ) : (
            <Mic className="w-6 h-6 text-purple-400" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate">{session.title}</h3>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(session.recorded_at).toLocaleDateString()}
            </span>
            <span className="capitalize">{session.recording_type}</span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => onPlay(session)} size="sm" variant="outline">
            <Play className="w-4 h-4" />
          </Button>
          <Button onClick={() => onDelete(session)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Playback Modal
const PlaybackModal = ({ session, onClose }) => {
  if (!session) return null;
  
  const isVideo = session.recording_type === "video";
  const mediaUrl = `${API.replace('/api', '')}${session.recording_url}`;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-bold">{session.title}</h2>
          <Button onClick={onClose} variant="ghost" size="sm">
            <XCircle className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="p-4">
          {isVideo ? (
            <video 
              src={mediaUrl}
              controls
              autoPlay
              className="w-full rounded-lg"
            />
          ) : (
            <div className="flex flex-col items-center py-8">
              <Mic className="w-20 h-20 text-purple-400 mb-4" />
              <audio 
                src={mediaUrl}
                controls
                autoPlay
                className="w-full max-w-md"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Practice Mode Page
const PracticeMode = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState("menu"); // menu, recording, sessions
  const [recordingType, setRecordingType] = useState(null); // video or voice
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [playbackSession, setPlaybackSession] = useState(null);
  const [savingTitle, setSavingTitle] = useState("");

  // Fetch saved sessions
  const fetchSessions = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/practice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data);
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [token]);

  const startRecording = async (type) => {
    setRecordingType(type);
    
    try {
      const res = await axios.post(`${API}/practice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSession(res.data);
      setMode("recording");
    } catch (err) {
      toast.error("Failed to start practice session");
    }
  };

  const handleRecordingComplete = async (blob, duration) => {
    if (!currentSession) return;
    
    const formData = new FormData();
    formData.append('recording', blob, 'recording.webm');
    formData.append('recording_type', recordingType);
    formData.append('title', savingTitle || `Practice ${new Date().toLocaleDateString()}`);
    
    try {
      await axios.post(`${API}/practice/${currentSession.session_id}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Save the session
      await axios.post(`${API}/practice/${currentSession.session_id}/save`, {
        title: savingTitle || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Practice session saved!");
      setMode("sessions");
      fetchSessions();
    } catch (err) {
      toast.error("Failed to save practice session");
    }
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm("Delete this practice session?")) return;
    
    try {
      await axios.delete(`${API}/practice/${session.session_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Practice session deleted");
      setSessions(prev => prev.filter(s => s.session_id !== session.session_id));
    } catch (err) {
      toast.error("Failed to delete session");
    }
  };

  const handleCancel = async () => {
    if (currentSession) {
      try {
        await axios.delete(`${API}/practice/${currentSession.session_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        // Ignore error
      }
    }
    setCurrentSession(null);
    setMode("menu");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Please sign in to use Practice Mode</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Practice Mode</h1>
            <p className="text-muted-foreground">Rehearse your arguments before the real debate</p>
          </div>
          
          {mode !== "menu" && (
            <Button onClick={() => setMode("menu")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        {/* Menu Mode */}
        {mode === "menu" && (
          <div className="space-y-6">
            {/* Start New Practice */}
            <div className="bg-card/30 rounded-lg border border-white/10 p-6">
              <h2 className="font-bold text-lg mb-4">Start a Practice Session</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => startRecording("video")}
                  className="p-6 rounded-lg border-2 border-white/10 hover:border-blue-500/50 hover:bg-blue-500/10 transition-all text-left group"
                >
                  <Video className="w-10 h-10 text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold mb-1">🎥 Video Practice</h3>
                  <p className="text-sm text-muted-foreground">Record with camera + microphone</p>
                </button>
                
                <button
                  onClick={() => startRecording("voice")}
                  className="p-6 rounded-lg border-2 border-white/10 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all text-left group"
                >
                  <Mic className="w-10 h-10 text-purple-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold mb-1">🎙 Voice Practice</h3>
                  <p className="text-sm text-muted-foreground">Record audio only</p>
                </button>
              </div>
            </div>
            
            {/* Saved Sessions */}
            <div className="bg-card/30 rounded-lg border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-lg">Saved Sessions</h2>
                {sessions.length > 0 && (
                  <span className="text-sm text-muted-foreground">{sessions.length} saved</span>
                )}
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Play className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No saved practice sessions yet</p>
                  <p className="text-sm">Start recording to create one!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map(session => (
                    <PracticeSessionCard 
                      key={session.session_id}
                      session={session}
                      onPlay={setPlaybackSession}
                      onDelete={handleDeleteSession}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recording Mode */}
        {mode === "recording" && (
          <div className="space-y-4">
            <div className="bg-card/30 rounded-lg border border-white/10 p-4 mb-4">
              <Label htmlFor="title">Session Title (optional)</Label>
              <Input
                id="title"
                value={savingTitle}
                onChange={(e) => setSavingTitle(e.target.value)}
                placeholder={`Practice ${new Date().toLocaleDateString()}`}
                className="mt-2 bg-zinc-900/50 border-white/10"
              />
            </div>
            
            <PracticeRecorder 
              recordingType={recordingType}
              onRecordingComplete={handleRecordingComplete}
              onCancel={handleCancel}
            />
          </div>
        )}

        {/* Sessions Mode */}
        {mode === "sessions" && (
          <div className="space-y-3">
            {sessions.map(session => (
              <PracticeSessionCard 
                key={session.session_id}
                session={session}
                onPlay={setPlaybackSession}
                onDelete={handleDeleteSession}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Playback Modal */}
      {playbackSession && (
        <PlaybackModal 
          session={playbackSession}
          onClose={() => setPlaybackSession(null)}
        />
      )}
    </div>
  );
};

export default PracticeMode;
